// src/components/LoginForm.js
import React, { useEffect, useState, useRef } from 'react';
import { login } from '../api';
import { useNavigate } from 'react-router-dom';
import SeoHead from './SeoHead';

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const navigate = useNavigate();
  const navigateTimerRef = useRef(null);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    return () => {
      if (navigateTimerRef.current) {
        clearTimeout(navigateTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await login(username, password);
      // Token is set as httpOnly cookie by the server — never stored in JS
      // Store display name only (not sensitive)
      localStorage.setItem('username', res.data.username);
      localStorage.setItem('companyId', res.data.companyId ?? '');

      // Notify and redirect (delay navigation so toast stays visible)
      setToast('Login successful – du er nu logget ind.');
      window.dispatchEvent(new Event('token-changed'));
      navigateTimerRef.current = setTimeout(() => navigate('/'), 1800);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    }
  };

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Log ind | AICE platform"
        description="Log ind for at få adgang til AICE-platformens AI-agenter og værktøjer."
        canonicalPath="/login"
        robots="noindex,nofollow"
      />
      {toast && (
        <div style={toastStyle} role="status">
          {toast}
        </div>
      )}
      <div style={cardStyle}>
        <h2 style={titleStyle}>Log ind</h2>
        <p style={subtitleStyle}>Adgang til AICE platformen</p>
        {error && <p style={errorStyle}>{error}</p>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            <label style={labelStyle}>Brugernavn</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={inputStyle}
              placeholder="Indtast brugernavn"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            <label style={labelStyle}>Adgangskode</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={inputStyle}
              placeholder="Indtast adgangskode"
            />
          </div>
          <button type="submit" style={submitStyle}>Log ind</button>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;

const pageStyle = {
  minHeight: '80vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, #FFF2EE 0%, #FFFFFF 70%)',
  padding: '2rem 1rem',
};

const cardStyle = {
  width: '100%',
  maxWidth: '440px',
  background: '#fff',
  borderRadius: '16px',
  padding: '2.25rem',
  boxShadow: '0 18px 38px rgba(192, 54, 38, 0.12)',
  border: '1px solid #F5DAD3',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.8rem',
  color: '#2A0D05',
};

const subtitleStyle = {
  margin: '0.25rem 0 1rem',
  color: '#5C3A32',
};

const labelStyle = {
  fontWeight: 600,
  color: '#2A0D05',
  fontSize: '0.95rem',
};

const inputStyle = {
  padding: '0.65rem 0.75rem',
  borderRadius: '10px',
  border: '1px solid #EBC4BB',
  background: '#FFF7F5',
  fontSize: '1rem',
  outline: 'none',
  color: '#2A0D05',
};

const submitStyle = {
  marginTop: '0.5rem',
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  padding: '0.75rem 1.25rem',
  borderRadius: '10px',
  border: 'none',
  fontWeight: 700,
  fontSize: '1.05rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const errorStyle = {
  color: '#C03626',
  background: '#FFF0EC',
  border: '1px solid #F5DAD3',
  padding: '0.65rem 0.75rem',
  borderRadius: '10px',
  marginTop: 0,
};

const toastStyle = {
  position: 'fixed',
  top: '1.25rem',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'linear-gradient(135deg, #99ffb3ff, #32c955ff)',
  color: '#fff',
  padding: '0.75rem 1.1rem',
  borderRadius: '10px',
  boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
  zIndex: 1200,
  fontWeight: 700,
};
